import { isUnreplacedMacro } from 'common/services/ogury-callback/ogury-motionly-settings';
import { ParametersExtractor } from '../parameters-extractor/parameters-extractor';

// advert_id is the /ocs grain key: a wrong one merges two ads, so read no
// further than the engine did (own window + parent). creative_id walks the
// whole chain, as a DSP ADM can nest the creative one iframe deeper (ADR 0002).
// When a DSP nests it deeper still, advert_id and campaignid come from the
// context that holds creativeId: ms-ads writes the three together in the
// slot's MRAID tag window (ADV-33078).
const MACRO_TO_CONTEXT = {
  advert_id: { contextKey: 'impressionId', maxHops: 2, besideCreativeId: true },
  campaignid: { contextKey: 'campaignId', maxHops: 0, besideCreativeId: true },
  creative_id: {
    contextKey: 'creativeId',
    maxHops: undefined,
    besideCreativeId: false
  }
} as const;

export type IdMacro = keyof typeof MACRO_TO_CONTEXT;

export type ResolvedMacro<V> = {
  value: V;
  // True when the value came from the context that holds creativeId.
  fromCreativeContext: boolean;
};

/** details.macro_source when an id came from the context that holds creativeId. */
export const CREATIVE_CONTEXT_MACRO_SOURCE = 'ancestor';

function isUsable(
  value: string | number | undefined
): value is string | number {
  return value !== undefined && value !== '';
}

/** {@link resolveMacro}, plus whether the value came from the creativeId context. */
export function resolveMacroWithSource(
  macroKey: IdMacro,
  macroValue: string,
  parametersExtractor: ParametersExtractor
): ResolvedMacro<string>;
export function resolveMacroWithSource(
  macroKey: IdMacro,
  macroValue: string | undefined,
  parametersExtractor: ParametersExtractor
): ResolvedMacro<string | undefined>;
export function resolveMacroWithSource(
  macroKey: IdMacro,
  macroValue: string | undefined,
  parametersExtractor: ParametersExtractor
): ResolvedMacro<string | undefined> {
  if (!isUnreplacedMacro(macroValue, macroKey)) {
    return { value: macroValue, fromCreativeContext: false };
  }

  const { contextKey, maxHops, besideCreativeId } = MACRO_TO_CONTEXT[macroKey];
  const fromContext = parametersExtractor.getContextParameters<string | number>(
    contextKey,
    window,
    undefined,
    maxHops
  );
  if (isUsable(fromContext)) {
    return { value: String(fromContext), fromCreativeContext: false };
  }

  if (besideCreativeId) {
    const fromCreativeContext = parametersExtractor.getContextParameterBeside<
      string | number
    >(contextKey, 'creativeId', window);
    if (isUsable(fromCreativeContext)) {
      return { value: String(fromCreativeContext), fromCreativeContext: true };
    }
  }

  return { value: macroValue, fromCreativeContext: false };
}

/** Recovers an id macro the ad server left unreplaced from the ad context (ADR 0002). */
export function resolveMacro(
  macroKey: IdMacro,
  macroValue: string,
  parametersExtractor: ParametersExtractor
): string;
export function resolveMacro(
  macroKey: IdMacro,
  macroValue: string | undefined,
  parametersExtractor: ParametersExtractor
): string | undefined;
export function resolveMacro(
  macroKey: IdMacro,
  macroValue: string | undefined,
  parametersExtractor: ParametersExtractor
): string | undefined {
  return resolveMacroWithSource(macroKey, macroValue, parametersExtractor)
    .value;
}
